/**
 * Retail barcodes (GTIN-8/12/13/14): validation, normalisation and UPC-E expansion. Pure functions.
 *
 * Scanners report what is printed: 12-digit UPC-A in North America, 13-digit EAN-13 elsewhere,
 * 8-digit EAN-8 or UPC-E on small packs. Open Food Facts files products under their 13-digit code
 * (UPC-A with a leading 0), so lookups try the normalised code first and the UPC-E expansion next.
 */

/** GS1 mod-10 check digit for the digits before it (weights 3,1,3,1... from the right). */
export function gs1CheckDigit(body: string): number {
  let sum = 0;
  for (let i = body.length - 1, w = 3; i >= 0; i--, w = w === 3 ? 1 : 3) sum += Number(body[i]) * w;
  return (10 - (sum % 10)) % 10;
}

export function hasValidCheckDigit(code: string): boolean {
  if (!/^\d{8}$|^\d{12,14}$/.test(code)) return false;
  return gs1CheckDigit(code.slice(0, -1)) === Number(code[code.length - 1]);
}

/**
 * UPC-E (8 digits: number system 0/1, six data digits, check digit) to its 12-digit UPC-A form.
 * Returns null when the code cannot be a UPC-E.
 */
export function expandUpcE(code: string): string | null {
  if (!/^[01]\d{7}$/.test(code)) return null;
  const ns: string = code[0]!;
  const d: string = code.slice(1, 7);
  const check: string = code[7]!;
  const last: string = d[5]!;
  let body: string;
  if (last === '0' || last === '1' || last === '2') body = `${d[0]}${d[1]}${last}0000${d[2]}${d[3]}${d[4]}`;
  else if (last === '3') body = `${d[0]}${d[1]}${d[2]}00000${d[3]}${d[4]}`;
  else if (last === '4') body = `${d[0]}${d[1]}${d[2]}${d[3]}00000${d[4]}`;
  else body = `${d[0]}${d[1]}${d[2]}${d[3]}${d[4]}0000${last}`;
  const upcA: string = `${ns}${body}${check}`;
  return hasValidCheckDigit(upcA) ? upcA : null;
}

export type GtinResult =
  | { ok: true; gtin: string; candidates: string[] }
  | { ok: false; reason: string };

/**
 * Cleans a scanned or typed code and lists the codes worth looking up, best first.
 * `gtin` is the canonical form stored with a saved food: 13 digits for UPC-A/EAN-13, else as printed.
 */
export function parseGtin(raw: string): GtinResult {
  const code: string = raw.replace(/[\s-]/g, '');
  if (!/^\d+$/.test(code)) return { ok: false, reason: 'A barcode is made of digits only.' };
  if (![8, 12, 13, 14].includes(code.length)) return { ok: false, reason: 'A retail barcode has 8, 12, 13 or 14 digits.' };

  const candidates: string[] = [];
  const add = (c: string | null): void => { if (c !== null && !candidates.includes(c)) candidates.push(c); };

  if (code.length === 8) {
    // Eight digits is either EAN-8 or UPC-E; try both readings that pass their check digit.
    const upcA: string | null = expandUpcE(code);
    if (hasValidCheckDigit(code)) add(code);
    if (upcA !== null) { add(`0${upcA}`); add(upcA); }
    if (candidates.length === 0) return { ok: false, reason: 'The check digit does not match. Scan again or check the number.' };
    return { ok: true, gtin: candidates[0]!, candidates };
  }

  if (!hasValidCheckDigit(code)) return { ok: false, reason: 'The check digit does not match. Scan again or check the number.' };
  if (code.length === 12) { add(`0${code}`); add(code); }
  else if (code.length === 14 && code.startsWith('0')) { add(code.slice(1)); add(code); }
  else add(code);
  return { ok: true, gtin: candidates[0]!, candidates };
}

/**
 * A code from a product database (USDA FDC gtin_upc) -> the canonical form a scan is stored under.
 * Databases often drop leading zeros ("16000275287"), so short codes are zero-padded to UPC-A first.
 * Null when no reading passes the check digit.
 */
export function canonicalGtin(raw: string | null | undefined): string | null {
  const digits: string = (raw ?? '').replace(/\D/g, '');
  if (digits.length < 8 || digits.length > 14) return null;
  const tries: string[] = [digits];
  if (digits.length > 8 && digits.length < 12) tries.push(digits.padStart(12, '0'));
  if (digits.length === 14 && digits.startsWith('00')) tries.push(digits.slice(2));
  for (const t of tries) {
    const r: GtinResult = parseGtin(t);
    if (r.ok) return r.gtin;
  }
  return null;
}
